import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  useTheme,
  Grid,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  TextField
} from '@mui/material';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { userApi } from 'api';
import { personalDataStore } from 'stores/personalDataStore';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocketContext } from 'contexts/SocketContext';

const PaymentHrInvitationsPage = () => {
  const { formId } = useParams<{ formId: string }>();
  const [priceRubs, setPrices] = useState<{ invitations: number; priceRub: number }[]>([]);
  const [selectedTariff, setSelectedTariff] = useState<{
    invitations: number;
    priceRub: number;
  } | null>(null);
  const navigate = useNavigate();
  const { paymentMessages: messages, socket } = useSocketContext();
  //   const [error, setError] = useState<string | null>(null);
  //   const [isSubmitted, setIsSubmitted] = useState(false);

  const isSubmitted = false;
  const [successInfo, setSuccessInfo] = useState<{
    addedInvitations: number;
    newBalance: number;
  } | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoInvitations, setPromoInvitations] = useState<number | null>(null);

  // Ссылку формирует бэкенд: сумма пакета, Receipt (54-ФЗ) и подпись — на сервере.
  const handlePayment = async () => {
    if (!formId || !selectedTariff) return;
    setPaymentOpen(true);
    setWidgetLoading(true);
    setPaymentUrl(null);
    try {
      const url = await userApi.getRobokassaUrl({
        formId,
        paymentType: 'hr_invitation',
        pack: selectedTariff.invitations
      });
      setPaymentUrl(url);
    } catch (e) {
      console.error(e);
      setPaymentOpen(false);
      setWidgetLoading(false);
    }
  };

  const handleClosePayment = () => {
    setPaymentOpen(false);
    setPaymentUrl(null);
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const data = await userApi.applyPromoCode({
        code,
      });
      // В ответе приходит количество начисленных приглашений — показываем его юзеру.
      setPromoInvitations(data.invitations ?? 0);
    } catch (e) {
      console.error(e);
      setPromoError('Не удалось применить промокод. Проверьте код и попробуйте ещё раз.');
    } finally {
      setPromoLoading(false);
    }
  };

  useEffect(() => {
    personalDataStore.getPayments();
  }, []);

  useEffect(() => {
    const matched = messages.find((i) => i.formId === formId);
    if (matched) {
      setPaymentOpen(false);
      setPaymentUrl(null);
      const added = matched.invitations ?? selectedTariff?.invitations ?? 0;
      userApi.getHrInvitationsBalance().then((data) => {
        setSuccessInfo({ addedInvitations: added, newBalance: data.balance });
      });
    }
  }, [messages, formId, selectedTariff?.invitations]);

  const formIsAvailable = useMemo(
    () => !personalDataStore.payments.find((i) => i.formId === formId) || !socket?.active,
    [formId, socket?.active]
  );

  useEffect(() => {
    personalDataStore.getPayments();
    userApi.getHrInvitationsPrices().then((data) => {
      setPrices(data);
      if (Array.isArray(data) && data.length > 0) {
        setSelectedTariff(data[0]);
      }
    });
  }, []);

  const theme = useTheme();
  const hasTariffs = priceRubs.length > 0;

  return (
    <Box
      sx={{ height: '100%', position: 'relative', overflow: 'auto' }}
      alignItems="center"
      justifyContent="center"
    >
      <Box
        sx={{
          width: '100%',
          p: 7,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Card sx={{ maxWidth: '60vw' }}>
          <CardContent sx={{ p: 4 }}>
            {successInfo ? (
              <Stack spacing={3} alignItems="center" textAlign="center">
                {/* Success icon */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: `0 8px 24px ${theme.palette.success.main}40`
                  }}
                >
                  <Icon icon="mdi:check-circle-outline" width={44} height={44} />
                </Box>

                <Typography variant="h5" fontWeight={700}>
                  Спасибо за оплату!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Пакет успешно зачислен на ваш аккаунт.
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    px: 3,
                    py: 2.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.success.main + '12',
                    border: `1.5px solid ${theme.palette.success.main}40`,
                  }}
                >
                  <Stack spacing={1.5} divider={<Divider flexItem />}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Добавлено интервью
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        +{successInfo.addedInvitations}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Текущий баланс
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {successInfo.newBalance} интервью
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Button
                  size="large"
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/hr/company')}
                  startIcon={<Icon icon="mdi:arrow-left" />}
                >
                  Вернуться на главный экран
                </Button>
              </Stack>
            ) : (
            <Stack spacing={3} alignItems="center" textAlign="center">
              {/* Icon */}
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}
              >
                <Icon icon="mdi:credit-card-check-outline" width={36} height={36} />
              </Box>

              {/* Title */}
              <Typography variant="h5" fontWeight={700}>
                Оформить пакет интервью для новых кандидатов
              </Typography>

              <Box
                sx={{
                  mb: '32px !important',
                  px: 3,
                  py: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.primary.main + '14',
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  textAlign: 'left'
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                  Приглашайте без риска
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Кандидат не прошёл интервью за 5 дней — вы ничего не теряете.
                </Typography>
              </Box>

              {/* Description
              <Typography variant="body1" color="text.secondary">
                Чем больше интервью в пакете, тем выгоднее стоимость одного интервью.
              </Typography> */}

              {/* Tariff cards */}
              <Box sx={{ width: '100%', pt: 1 }}>
                {hasTariffs ? (
                  <Grid container spacing={2}>
                    {priceRubs.map((item) => {
                      const isSelected =
                        selectedTariff?.invitations === item.invitations &&
                        selectedTariff.priceRub === item.priceRub;
                      const priceRubPerInvitation =
                        item.invitations > 0
                          ? Math.round(item.priceRub / item.invitations)
                          : item.priceRub;

                      return (
                        <Grid item xs={12} md key={`${item.invitations}-${item.priceRub}`}>
                          <Card
                            onClick={() => {
                              if (isSubmitted) return;
                              setSelectedTariff(item);
                            }}
                            sx={{
                              height: '100%',
                              cursor: isSubmitted ? 'default' : 'pointer',
                              borderRadius: 2,
                              borderWidth: 2,
                              borderStyle: 'solid',
                              borderColor: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                              boxShadow: isSelected ? theme.shadows[12] : 'unset',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: isSubmitted ? theme.shadows[1] : theme.shadows[6],
                                transform: isSubmitted ? 'none' : 'translateY(-2px)'
                              }
                            }}
                          >
                            <CardContent sx={{ p: 2, height: '100%' }}>
                              <Stack spacing={1.5}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  justifyContent="center"
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    textAlign="center"
                                  >
                                    {item.invitations} интервью
                                  </Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight={700} color="#6558F5">
                                  {item.priceRub.toLocaleString('ru-RU')} ₽
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ~ {priceRubPerInvitation.toLocaleString('ru-RU')} ₽ за интервью
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Тарифы пока недоступны. Попробуйте обновить страницу позже.
                  </Typography>
                )}
              </Box>

              {/* Selected tariff summary */}
              {selectedTariff && (
                <Box sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Вы выбрали пакет на{' '}
                    <Typography component="span" fontWeight={600}>
                      {selectedTariff.invitations} интервью
                    </Typography>{' '}
                    за{' '}
                    <Typography component="span" fontWeight={600}>
                      {selectedTariff.priceRub.toLocaleString('ru-RU')} ₽
                    </Typography>
                    .
                  </Typography>
                </Box>
              )}

              {/* Промокод */}
              <Box sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    label="Промокод"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      setPromoError(null);
                    }}
                    disabled={promoLoading}
                    error={!!promoError}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleApplyPromo}
                    disabled={!promoCode.trim() || promoLoading}
                    sx={{ minWidth: 120, height: 40, whiteSpace: 'nowrap' }}
                  >
                    {promoLoading ? <CircularProgress size={20} /> : 'Применить'}
                  </Button>
                </Stack>
                {promoError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, textAlign: 'left' }}
                  >
                    {promoError}
                  </Typography>
                )}
              </Box>

              {/* {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )} */}
              {!formIsAvailable && (
                <Typography color="error" variant="body1">
                  Данная форма оплаты недействительна!
                </Typography>
              )}
              {/* Pay button */}
              {formIsAvailable ? (
                <Button
                  size="large"
                  variant="contained"
                  fullWidth
                  disabled={!selectedTariff || isSubmitted}
                  onClick={handlePayment}
                  startIcon={<Icon icon="mdi:lock-check-outline" />}
                >
                  Оплатить и продолжить рассылку
                </Button>
              ) : (
                <Button
                  size="large"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={isSubmitted}
                  onClick={() => navigate('/main')}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    fontWeight: 600
                  }}
                >
                  Вернуться на главынй экран
                </Button>
              )}

              {/* Footer note */}
              <Typography variant="caption" color="text.secondary">
                Оплата производится через защищённый платёжный сервис
              </Typography>
            </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={paymentOpen}
        onClose={handleClosePayment}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: { xs: '100%', sm: '90vh' },
            maxHeight: { xs: '100%', sm: '90vh' },
            width: { xs: '100%', sm: '90vw' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 3 }
          }
        }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
          <IconButton onClick={handleClosePayment} aria-label="Закрыть">
            <Icon icon="mdi:close" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
          {widgetLoading && (
            <Stack
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                backgroundColor: 'background.paper'
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Загрузка формы оплаты…
              </Typography>
            </Stack>
          )}

          {paymentUrl && (
            <Box
              component="iframe"
              src={paymentUrl}
              title="Robokassa"
              onLoad={() => setWidgetLoading(false)}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Всплывающее окно об успешном применении промокода */}
      <Dialog
        open={promoInvitations !== null}
        onClose={() => setPromoInvitations(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: `0 8px 24px ${theme.palette.success.main}40`
              }}
            >
              <Icon icon="mdi:check-circle-outline" width={40} height={40} />
            </Box>

            <Typography variant="h6" fontWeight={700}>
              Промокод применён!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Поздравляем, вам начислено{' '}
              <Typography component="span" fontWeight={700} color="success.main">
                {promoInvitations} бесплатных приглашения
              </Typography>
              .
            </Typography>

            <Button
              size="large"
              variant="contained"
              fullWidth
              onClick={() => setPromoInvitations(null)}
            >
              Отлично
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default observer(PaymentHrInvitationsPage);
